import { r } from '@/theme/tokens';
import { Tooltip, type TooltipProps } from './Tooltip';
import type { TooltipOwnerState } from '@mui/material/Tooltip';
import type { ReactElement } from 'react';

export type CursorTooltipProps = TooltipProps;

export const CursorTooltip = ({
  enterDelay = 0,
  enterNextDelay = 0,
  followCursor = true,
  placement = 'right-start',
  slotProps,
  ...props
}: CursorTooltipProps): ReactElement => {
  const popperSlot = slotProps?.popper;
  const tooltipSlot = slotProps?.tooltip;
  const transitionSlot = slotProps?.transition;

  const resolvePopperSlot = (
    ownerState: TooltipOwnerState,
  ): NonNullable<CursorTooltipProps['slotProps']>['popper'] => {
    const resolved = typeof popperSlot === 'function' ? popperSlot(ownerState) : popperSlot;
    return {
      ...(resolved ?? {}),
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 3],
          },
        },
        ...(resolved?.modifiers ?? []),
      ],
    };
  };

  const resolveTooltipSlot = (
    ownerState: TooltipOwnerState,
  ): NonNullable<CursorTooltipProps['slotProps']>['tooltip'] => {
    const resolved = typeof tooltipSlot === 'function' ? tooltipSlot(ownerState) : tooltipSlot;
    const resolvedSx = resolved?.sx;
    return {
      ...(resolved ?? {}),
      sx: [
        {
          p: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          borderRadius: r('md'),
          boxShadow: 'none',
          maxWidth: 'min(calc(100vw - 32px), 900px)',
        },
        ...(Array.isArray(resolvedSx) ? resolvedSx : resolvedSx ? [resolvedSx] : []),
      ],
    };
  };

  const resolveTransitionSlot = (
    ownerState: TooltipOwnerState,
  ): NonNullable<CursorTooltipProps['slotProps']>['transition'] => {
    const resolved =
      typeof transitionSlot === 'function' ? transitionSlot(ownerState) : transitionSlot;
    return {
      ...(resolved ?? {}),
      timeout: 0,
    };
  };

  return (
    <Tooltip
      {...props}
      enterDelay={enterDelay}
      enterNextDelay={enterNextDelay}
      followCursor={followCursor}
      placement={placement}
      slotProps={{
        ...slotProps,
        popper: resolvePopperSlot,
        tooltip: resolveTooltipSlot,
        transition: resolveTransitionSlot,
      }}
    />
  );
};
